import React, { useState, useEffect } from 'react';
import { Gift, Copy, CheckCircle, Share2, Loader2, PartyPopper, Mail } from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { toast } from 'sonner';
import { getAuthHeaders } from '../context/AuthContext';

import { BACKEND_URL } from '../config/api';

// Social share helpers
const shareText = "I use Kura Stash to organize my craft supplies and get AI design ideas. Join free with my link!";

const shareToTwitter = (url) => {
  window.open(
    `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(url)}`,
    '_blank', 'width=550,height=420'
  );
};

const shareToFacebook = (url) => {
  window.open(
    `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`,
    '_blank', 'width=550,height=420'
  );
};

const shareViaEmail = (url) => {
  const subject = "Join me on Kura Stash!";
  const body = `Hey!\n\n${shareText}\n\n${url}\n\nIt's free to start with 20 items included!`;
  window.location.href = `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
};

const XIcon = ({ className }) => (
  <svg viewBox="0 0 24 24" className={className} fill="currentColor">
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
  </svg>
);

const FacebookIcon = ({ className }) => (
  <svg viewBox="0 0 24 24" className={className} fill="currentColor">
    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
  </svg>
);

const ReferralShareWidget = ({ variant = 'full', isUnlimited = false }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);

  useEffect(() => { fetchReferral(); }, []);

  const fetchReferral = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/billing/referral/code`, {
        credentials: 'include', headers: getAuthHeaders()
      });
      if (res.ok) setData(await res.clone().json());
    } catch { /* ignore */ }
    finally { setLoading(false); }
  };

  const referralUrl = data?.referral_code
    ? `${window.location.origin}/join/${data.referral_code}`
    : '';

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(referralUrl);
      setCopied(true);
      toast.success('Referral link copied!');
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error('Failed to copy');
    }
  };

  const shareNative = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: 'Join me on Kura Stash!',
          text: shareText,
          url: referralUrl,
        });
      } catch { /* user cancelled */ }
    } else {
      copyLink();
    }
  };

  if (loading) return null;
  if (!data) return null;

  const totalReferrals = data.referral_count || 0;
  const rewardsMaxed = data.rewards_maxed || false;
  const rewardCap = data.reward_cap || 20;
  const rewardEligible = data.reward_eligible || 0;
  const freeItemsEarned = data.free_items_earned || 0;
  const freeItemsCap = data.free_items_cap || 100;

  // Social share buttons row (reused in both variants)
  const ShareButtons = ({ size = 'sm' }) => (
    <div className="flex gap-2">
      <button onClick={() => shareToTwitter(referralUrl)}
        className={`flex-1 flex items-center justify-center gap-1.5 ${size === 'sm' ? 'py-1.5 text-xs' : 'py-2.5 px-4 text-sm'} rounded-lg bg-charcoal/90 text-white font-medium hover:bg-charcoal transition-colors`}
        data-testid={`share-twitter-${variant}`}>
        <XIcon className={size === 'sm' ? 'h-3 w-3' : 'h-4 w-4'} /> {size === 'sm' ? 'Post' : 'Post on X'}
      </button>
      <button onClick={() => shareToFacebook(referralUrl)}
        className={`flex-1 flex items-center justify-center gap-1.5 ${size === 'sm' ? 'py-1.5 text-xs' : 'py-2.5 px-4 text-sm'} rounded-lg bg-[#1877F2] text-white font-medium hover:bg-[#1565C0] transition-colors`}
        data-testid={`share-facebook-${variant}`}>
        <FacebookIcon className={size === 'sm' ? 'h-3 w-3' : 'h-4 w-4'} /> Share
      </button>
      <button onClick={() => shareViaEmail(referralUrl)}
        className={`flex-1 flex items-center justify-center gap-1.5 ${size === 'sm' ? 'py-1.5 text-xs' : 'py-2.5 px-4 text-sm'} rounded-lg bg-sage text-white font-medium hover:bg-sage-dark transition-colors`}
        data-testid={`share-email-${variant}`}>
        <Mail className={size === 'sm' ? 'h-3 w-3' : 'h-4 w-4'} /> Email
      </button>
      {navigator.share && (
        <button onClick={shareNative}
          className={`flex-1 flex items-center justify-center gap-1.5 ${size === 'sm' ? 'py-1.5 text-xs' : 'py-2.5 px-4 text-sm'} rounded-lg border border-beige text-charcoal font-medium hover:bg-cream transition-colors`}
          data-testid={`share-native-${variant}`}>
          <Share2 className={size === 'sm' ? 'h-3 w-3' : 'h-4 w-4'} /> More
        </button>
      )}
    </div>
  );

  // Compact variant for dashboard
  if (variant === 'compact') {
    return (
      <div className="bg-white rounded-xl border border-beige p-4" data-testid="referral-widget-compact">
        <div className="flex items-center gap-3 mb-3">
          <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${rewardsMaxed ? 'bg-sage-light/60' : 'bg-sage-light/40'}`}>
            {rewardsMaxed ? <PartyPopper className="h-4 w-4 text-sage" /> : <Gift className="h-4 w-4 text-sage" />}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-charcoal">
              {rewardsMaxed ? `${totalReferrals} friends joined!` : 'Invite friends, earn free items'}
            </p>
            <p className="text-xs text-gray-soft">
              {isUnlimited
                ? `${totalReferrals} friend${totalReferrals !== 1 ? 's' : ''} joined via your link`
                : rewardsMaxed
                  ? `Earned ${freeItemsEarned} bonus items. Keep sharing!`
                  : `${totalReferrals} joined, ${freeItemsEarned} items earned`
              }
            </p>
          </div>
        </div>
        <div className="flex gap-2 mb-2">
          <div className="flex-1 bg-cream rounded-lg px-3 py-1.5 text-xs font-mono text-charcoal truncate" data-testid="referral-url-compact">
            {referralUrl}
          </div>
          <Button size="sm" variant="outline" onClick={copyLink} className="flex-shrink-0" data-testid="copy-referral-link-compact">
            {copied ? <CheckCircle className="h-3.5 w-3.5 text-sage" /> : <Copy className="h-3.5 w-3.5" />}
          </Button>
        </div>
        <ShareButtons size="sm" />
      </div>
    );
  }

  // Full variant for billing page — always show share, distinguish earned vs total
  return (
    <Card className="bg-white border border-beige shadow-sm" data-testid="referral-section">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          {rewardsMaxed ? <PartyPopper className="h-5 w-5 text-sage" /> : <Gift className="h-5 w-5 text-sage" />}
          {rewardsMaxed ? 'Thank You for Sharing!' : 'Invite Friends, Earn Free Items'}
        </CardTitle>
        <CardDescription>
          {rewardsMaxed
            ? `You've earned the maximum ${freeItemsEarned} bonus items from ${rewardCap} referrals. Keep sharing — every new crafter helps the community!`
            : `Share your personal link. Each friend who joins earns you ${data.free_items_per_referral} permanent free item slots!`
          }
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Referral URL with copy */}
        <div className="space-y-2">
          <label className="text-xs text-gray-soft font-medium uppercase tracking-wide">Your referral link</label>
          <div className="flex gap-2">
            <div className="flex-1 bg-cream rounded-lg px-4 py-2.5 text-sm font-mono text-charcoal truncate border border-beige" data-testid="referral-url-full">
              {referralUrl}
            </div>
            <Button variant="outline" onClick={copyLink} data-testid="copy-referral-link-full">
              {copied ? <CheckCircle className="h-4 w-4 text-sage" /> : <Copy className="h-4 w-4" />}
              <span className="ml-1.5 hidden sm:inline">{copied ? 'Copied' : 'Copy'}</span>
            </Button>
          </div>
        </div>

        {/* Social share buttons */}
        <div className="space-y-2">
          <label className="text-xs text-gray-soft font-medium uppercase tracking-wide">Share via</label>
          <ShareButtons size="md" />
        </div>

        {/* Stats */}
        <div className={`grid ${isUnlimited ? 'grid-cols-1' : 'grid-cols-3'} gap-3 text-center`}>
          <div className="bg-cream rounded-lg p-3">
            <div className="text-xl font-light text-charcoal">{totalReferrals}</div>
            <div className="text-xs text-gray-soft">Friends Joined</div>
          </div>
          {!isUnlimited && (
            <>
              <div className="bg-cream rounded-lg p-3">
                <div className="text-xl font-light text-sage">{freeItemsEarned}</div>
                <div className="text-xs text-gray-soft">Items Earned</div>
              </div>
              <div className="bg-cream rounded-lg p-3">
                <div className="text-xl font-light text-charcoal">
                  {rewardsMaxed ? <span className="text-sage">Max</span> : rewardCap - rewardEligible}
                </div>
                <div className="text-xs text-gray-soft">{rewardsMaxed ? 'Rewards Maxed' : 'Rewards Left'}</div>
              </div>
            </>
          )}
        </div>

        {/* Progress toward reward cap (hidden for unlimited) */}
        {!isUnlimited && (
        <div>
          <div className="flex justify-between text-xs text-gray-soft mb-1">
            <span>{rewardEligible} of {rewardCap} reward-earning referrals</span>
            <span>{freeItemsEarned} / {freeItemsCap} bonus items</span>
          </div>
          <div className="w-full h-2 bg-beige rounded-full overflow-hidden">
            <div className="h-full bg-sage rounded-full transition-all duration-500"
              style={{ width: `${Math.min((rewardEligible / rewardCap) * 100, 100)}%` }} />
          </div>
          {rewardsMaxed && totalReferrals > rewardCap && (
            <p className="text-xs text-sage mt-1">
              + {totalReferrals - rewardCap} additional friend{totalReferrals - rewardCap !== 1 ? 's' : ''} joined via your link!
            </p>
          )}
        </div>
        )}
      </CardContent>
    </Card>
  );
};

export { ReferralShareWidget };
export default ReferralShareWidget;
